import React, { useState } from 'react'
import { CheckCircle, XCircle, BookOpen, AlertTriangle, AlertCircle, Maximize2, Minimize2 } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import AIButton from '../ai/AIButton'

interface TrueFalseSolutionDisplayProps {
  question: any
  userAnswer: any
  analysis: any
}

const TrueFalseSolutionDisplay: React.FC<TrueFalseSolutionDisplayProps> = ({
  question,
  userAnswer,
  analysis
}) => {
  const [imageExpanded, setImageExpanded] = useState(false)

  // Clean statement content by removing asterisks and extra whitespace
  const cleanStatementContent = (content: string): string => {
    if (!content) return content
    
    // Remove leading asterisks (*) and any following whitespace
    let cleaned = content.replace(/^\s*\*\s*/, '')
    
    // Remove any trailing asterisks
    cleaned = cleaned.replace(/\s*\*\s*$/, '')
    
    // Clean up extra whitespace
    cleaned = cleaned.trim()
    
    return cleaned
  }

  // Generate statements with real solutions from database (matches QuizScoringService logic)
  const generateStatements = (question: any) => {
    console.log('🔍 TrueFalseSolutionDisplay - Generating statements for question:', {
      id: question.id,
      lua_chon: question.lua_chon,
      statement_a: question.statement_a,
      statement_b: question.statement_b,
      statement_c: question.statement_c,
      statement_d: question.statement_d,
      answer_a: question.answer_a,
      answer_b: question.answer_b,
      answer_c: question.answer_c,
      answer_d: question.answer_d,
      dap_an_dung: question.dap_an_dung
    })

    if (question.statements && Array.isArray(question.statements)) {
      return question.statements
    }

    // Priority 1: Use statement_a/b/c/d from DS schema (matches QuizScoringService)
    const dsStatements = [
      { key: 'a', statement: question.statement_a, answer: question.answer_a, solution: question.solution_a },
      { key: 'b', statement: question.statement_b, answer: question.answer_b, solution: question.solution_b },
      { key: 'c', statement: question.statement_c, answer: question.answer_c, solution: question.solution_c },
      { key: 'd', statement: question.statement_d, answer: question.answer_d, solution: question.solution_d }
    ]

    const validDsStatements = dsStatements.filter(item => item.statement && item.statement.trim())
    if (validDsStatements.length > 0) {
      console.log('✅ TrueFalseSolutionDisplay - Using DS Schema statements from database')
      return validDsStatements.map((item, index) => {
        // Parse correct answer từ dấu sao (*) trong statement hoặc answer_a/b/c/d
        let correctAnswer = false
        
        // Priority 1: Check for asterisk (*) in statement content
        if (item.statement && typeof item.statement === 'string') {
          // Check if statement starts with asterisk (*)
          const hasAsterisk = /^\s*\*\s*/.test(item.statement)
          if (hasAsterisk) {
            correctAnswer = true
            console.log(`📝 TrueFalseSolutionDisplay - Statement ${item.key} marked as correct with asterisk (*): "${item.statement}"`)
          }
        }
        
        // Priority 2: Fallback to answer_a/b/c/d if no asterisk found
        if (!correctAnswer && item.answer !== undefined && item.answer !== null) {
          // DS Schema: answer có thể là 0/1, "0"/"1", true/false, "true"/"false", "Đúng"/"Sai"
          if (item.answer === 1 || item.answer === '1' || item.answer === true || item.answer === 'true' || item.answer === 'Đúng') {
            correctAnswer = true
          } else if (item.answer === 0 || item.answer === '0' || item.answer === false || item.answer === 'false' || item.answer === 'Sai') {
            correctAnswer = false
          }
          console.log(`📝 TrueFalseSolutionDisplay - Statement ${item.key} correct answer from answer field: ${correctAnswer} (from ${item.answer})`)
        }

        return {
          id: `${question.id}_statement_${item.key}`,
          content: cleanStatementContent(item.statement),
          correctAnswer,
          solution: item.solution || null
        }
      })
    }

    // Priority 2: Use lua_chon from database as statements
    if (question.lua_chon && question.lua_chon.length >= 4) {
      console.log('✅ TrueFalseSolutionDisplay - Using lua_chon statements from database')
      return question.lua_chon.slice(0, 4).map((statement: string, index: number) => {
        // Get solution from database
        const solutionKey = ['solution_a', 'solution_b', 'solution_c', 'solution_d'][index]
        const solution = question[solutionKey]
        
        // Parse correct answer từ dấu sao (*) trong statement hoặc dap_an_dung
        let correctAnswer = false

        // Priority 1: Check for asterisk (*) in statement content
        if (statement && typeof statement === 'string') {
          // Check if statement starts with asterisk (*)
          const hasAsterisk = /^\s*\*\s*/.test(statement)
          if (hasAsterisk) {
            correctAnswer = true
            console.log(`📝 TrueFalseSolutionDisplay - lua_chon[${index}] marked as correct with asterisk (*): "${statement}"`)
          }
        }
        
        // Priority 2: Fallback to dap_an_dung if no asterisk found
        if (!correctAnswer && question.dap_an_dung) {
          if (typeof question.dap_an_dung === 'string') {
            // Handle format like "ĐSSS" or "TFFF" or "1010"
            const answerStr = question.dap_an_dung.toUpperCase()
            if (answerStr.length > index) {
              const char = answerStr[index]
              correctAnswer = char === 'Đ' || char === 'T' || char === '1' || char === 'TRUE'
            }
          } else if (Array.isArray(question.dap_an_dung)) {
            // Handle array format like [true, false, false, false]
            correctAnswer = Boolean(question.dap_an_dung[index])
          } else if (typeof question.dap_an_dung === 'object') {
            // Handle object format
            const key = `statement_${index}`
            correctAnswer = Boolean(question.dap_an_dung[key])
          }
          console.log(`📝 TrueFalseSolutionDisplay - lua_chon[${index}] correct answer from dap_an_dung: ${correctAnswer} (from ${question.dap_an_dung})`)
        }
        
        return {
          id: `${question.id}_statement_${index}`,
          content: cleanStatementContent(statement),
          correctAnswer, // Real correct answer from database
          solution: solution || null // Real solution from database
        }
      })
    }

    // Fallback: Generate statements from main question if no lua_chon available
    const baseQuestion = question.cau_hoi || question.cauHoi || "Khái niệm vật lý cơ bản"
    
    return [
      {
        id: `${question.id}_statement_0`,
        content: baseQuestion,
        correctAnswer: true,
        solution: question.solution_a || null
      },
      {
        id: `${question.id}_statement_1`, 
        content: "Công thức liên quan được áp dụng trong mọi điều kiện",
        correctAnswer: false,
        solution: question.solution_b || null
      },
      {
        id: `${question.id}_statement_2`,
        content: "Hiện tượng này tuân theo các định luật vật lý cơ bản",
        correctAnswer: true,
        solution: question.solution_c || null
      },
      {
        id: `${question.id}_statement_3`,
        content: "Ứng dụng thực tế của khái niệm này rất hạn chế",
        correctAnswer: false,
        solution: question.solution_d || null
      }
    ]
  }

  const statements = generateStatements(question)
  
  // Calculate scoring details
  let correctStatements = 0
  let totalStatements = statements.length

  const statementResults = statements.map(statement => {
    const userStatementAnswer = userAnswer?.[statement.id]
    const isAnswered = userStatementAnswer !== null && userStatementAnswer !== undefined
    const userBool = userStatementAnswer === true || userStatementAnswer === 'true'
    const correctBool = statement.correctAnswer
    const isCorrect = isAnswered && userBool === correctBool

    if (isCorrect) {
      correctStatements++
    }

    return {
      ...statement,
      userAnswer: userStatementAnswer,
      isAnswered,
      userBool,
      correctBool,
      isCorrect
    }
  })

  // Calculate points based on correct statements
  let points = 0
  if (correctStatements === 1) points = 0.1
  else if (correctStatements === 2) points = 0.25
  else if (correctStatements === 3) points = 0.5
  else if (correctStatements >= 4) points = 1.0

  return (
    <div className="mt-2 space-y-2">
      {/* Question Content */}
      <div className="bg-gray-50 rounded p-3 relative group">
        <div className="flex items-center gap-2 mb-1.5">
          <BookOpen className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-gray-800 text-sm">Câu hỏi Đúng/Sai:</span>
        </div>
        <div className="text-sm text-gray-700">
          <KaTeXRenderer content={question.cau_hoi || question.cauHoi || question.question_lead || ''} />
        </div>
        
        {/* AI Button - Hiển thị ở chế độ xem lại */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <AIButton
            content={question}
            contentType="quiz"
            size="sm"
            variant="outline"
            title="Giải thích câu hỏi đúng/sai"
            className="bg-white border-blue-300 text-blue-600 hover:bg-blue-50"
            questionId={question.id}
            useDatabase={true}
          />
        </div>
        
        {/* Question Image with 50% screen mode */}
        {(question.hinh_anh || question.questionImage) && (
          <div className="mt-2 w-full overflow-hidden">
            <div className="relative w-full max-w-full">
              <img
                src={question.hinh_anh || question.questionImage}
                alt="Hình ảnh câu hỏi"
                className={`rounded border border-gray-200 transition-all duration-300 ${
                  imageExpanded 
                    ? 'fixed top-0 left-0 w-1/2 h-screen object-contain bg-black bg-opacity-90 z-50 cursor-pointer' 
                    : 'w-full h-auto max-h-[350px] md:max-h-[400px] cursor-pointer hover:opacity-80 mx-auto block object-contain'
                }`}
                style={{ maxWidth: imageExpanded ? '50%' : '100%' }}
                onClick={() => setImageExpanded(!imageExpanded)}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              
              {/* Image controls */}
              <button
                onClick={() => setImageExpanded(!imageExpanded)}
                className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all"
                title={imageExpanded ? 'Thu nhỏ ảnh' : 'Phóng to ảnh (50% màn hình)'}
              >
                {imageExpanded ? (
                  <Minimize2 className="w-4 h-4 text-gray-700" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-gray-700" />
                )}
              </button>
              
              {imageExpanded && (
                <div className="fixed top-0 right-0 w-1/2 h-screen bg-white overflow-y-auto z-50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Chi tiết câu hỏi</h3>
                    <button
                      onClick={() => setImageExpanded(false)}
                      className="bg-gray-200 hover:bg-gray-300 rounded-full p-2"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Câu hỏi:</h4>
                      <div className="bg-gray-50 p-3 rounded">
                        <KaTeXRenderer content={question.cau_hoi || question.cauHoi || ''} />
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">Kết quả của bạn:</h4>
                      <div className={`p-3 rounded ${
                        analysis.isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}>
                        {correctStatements}/{totalStatements} ý đúng - {analysis.points} điểm
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Result Info - Show completion status */}
      <div className="bg-blue-50 border border-blue-200 rounded p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {analysis.isCorrect ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600" />
            )}
            <span className="text-sm text-gray-800 font-medium">
              Kết quả: {correctStatements}/{totalStatements} ý đúng
            </span>
          </div>
          <span className="text-xs text-gray-600">{analysis.points}/{question.diem || 1} điểm</span>
        </div>
      </div>

      {/* Statement-by-Statement Analysis - Compact Format */}
      <div className="space-y-1.5">
        {statementResults.map((statement, index) => (
          <div
            key={statement.id}
            className={`border rounded p-2 ${
              statement.isCorrect 
                ? 'border-green-200 bg-green-50' 
                : statement.isAnswered 
                  ? 'border-red-200 bg-red-50'
                  : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="mb-1.5">
              <div className="flex items-center gap-1 mb-1">
                <span className="font-medium text-gray-700 text-sm">Ý {index + 1}:</span>
                {statement.isCorrect ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                ) : statement.isAnswered ? (
                  <XCircle className="w-3.5 h-3.5 text-red-600" />
                ) : null}
              </div>
              <div className="text-sm text-gray-700">
                <KaTeXRenderer content={statement.content} />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mb-1.5">
              <div>
                <span className="text-gray-600">Đáp án:</span>
                <span className={`ml-1 font-medium ${
                  statement.correctBool ? 'text-green-600' : 'text-red-600'
                }`}>
                  {statement.correctBool ? 'Đúng' : 'Sai'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Bạn chọn:</span>
                <span className={`ml-1 font-medium ${
                  statement.isAnswered 
                    ? statement.userBool ? 'text-blue-600' : 'text-orange-600'
                    : 'text-gray-400'
                }`}>
                  {statement.isAnswered 
                    ? (statement.userBool ? 'Đúng' : 'Sai')
                    : 'Chưa chọn'
                  }
                </span>
              </div>
              <div>
                <span className="text-gray-600">Kết quả:</span>
                <span className={`ml-1 font-medium ${
                  statement.isCorrect ? 'text-green-600' : 'text-red-600'
                }`}>
                  {statement.isCorrect ? 'Đúng' : 'Sai'}
                </span>
              </div>
            </div>
            
            {/* Individual statement solution from database */}
            {statement.solution && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                <div className="font-medium text-blue-800 mb-1 text-xs">Lời giải:</div>
                <div className="text-xs text-blue-700">
                  <KaTeXRenderer content={statement.solution} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Overall Solution */}
      {(analysis.explanation || question.loi_giai) && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-800 text-sm">Lời giải tổng quát:</span>
          </div>
          <div className="text-sm text-blue-700">
            <KaTeXRenderer content={analysis.explanation || question.loi_giai} />
          </div>
        </div>
      )}
    </div>
  )
}

export default TrueFalseSolutionDisplay